import { useContext } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useInternalBooking = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, setError, sendRequest } = useHttp();

  const getDaysOfMonth = async (
    entertainmentId,
    productId,
    year,
    month,
    overbooking = false,
    resourceId = null
  ) => {
    try {
      const resourceParam = resourceId ? `&resourceId=${resourceId}` : '';
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/${productId}/availableDays/${year}/${month}?overbooking=${overbooking}${resourceParam}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getCategories = async (entertainmentId, productId, year, month, day, resourceId = null) => {
    try {
      const resourceParam = resourceId ? `?resourceId=${resourceId}` : '';
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/${productId}/categories/${year}/${month}/${day}${resourceParam}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getDurations = async (
    entertainmentId,
    productId,
    year,
    month,
    day,
    categories,
    overbooking = false,
    resourceId = null
  ) => {
    try {
      const resourceParam = resourceId ? `&resourceId=${resourceId}` : '';
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/${productId}/${year}/${month}/${day}/durations?overbooking=${overbooking}${resourceParam}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { categories },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getStartTimes = async (
    entertainmentId,
    productId,
    year,
    month,
    day,
    categories,
    duration,
    overbooking = false,
    resourceId = null
  ) => {
    try {
      const resourceParam = resourceId ? `&resourceId=${resourceId}` : '';
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/${productId}/${year}/${month}/${day}/startTimes?overbooking=${overbooking}${resourceParam}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { categories, duration },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Build the query string for the packages/upsells endpoints. entertainmentId must be sent so
  // the backend can apply entertainment-scoped price overrides (product.packageOverrides /
  // upsellOverrides); without it the base upgrade price is returned instead of the venue price.
  const buildUpgradeQuery = (currentTags = [], entertainmentId = null) => {
    const parts = [];
    if (Array.isArray(currentTags) && currentTags.length > 0) {
      parts.push(`currentTags=${JSON.stringify(currentTags)}`);
    }
    if (entertainmentId) {
      parts.push(`entertainmentId=${entertainmentId}`);
    }
    return parts.length > 0 ? `?${parts.join('&')}` : '';
  };

  const getPackages = async (productId, currentTags = [], entertainmentId = null) => {
    try {
      const queryParams = buildUpgradeQuery(currentTags, entertainmentId);
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${productId}/packages${queryParams}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getUpsells = async (productId, currentTags = [], entertainmentId = null) => {
    try {
      const queryParams = buildUpgradeQuery(currentTags, entertainmentId);
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${productId}/upsells${queryParams}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const convertTemporaryReservationToBooking = async (
    temporaryReservationId,
    customerData,
    totalAmount,
    paymentType,
    priceOverrides,
    specialLineItems,
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null,
    comment = null,
    walkIn = false,
    confirmIfPending = false,
    disableSelfService = false,
    capacityParticipantCount = undefined,
    capacityLaneCount = undefined,
    suppressCustomerEmails = false
  ) => {
    try {
      const body = {
        customerData,
        totalAmount,
        paymentType,
        priceOverrides,
        specialLineItems,
        comment,
        walkIn,
        confirmIfPending,
        disableSelfService,
        suppressCustomerEmails,
      };
      if (
        (typeof capacityParticipantCount === 'number' && Number.isFinite(capacityParticipantCount)) ||
        (typeof capacityLaneCount === 'number' && Number.isFinite(capacityLaneCount))
      ) {
        if (typeof capacityParticipantCount === 'number' && Number.isFinite(capacityParticipantCount)) {
          body.capacityParticipantCount = capacityParticipantCount;
        }
        if (typeof capacityLaneCount === 'number' && Number.isFinite(capacityLaneCount)) {
          body.capacityLaneCount = capacityLaneCount;
        }
      }

      const responseData = await sendRequest(
        {
          url: `/api/v1/temporaryReservation/${temporaryReservationId}/convert`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err; // Re-throw the error to be caught by the calling component
    }
  };

  const invalidateTemporaryReservation = async temporaryReservationId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/temporaryReservation/${temporaryReservationId}/invalidate`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateInternalBooking = async (
    bookingId,
    update,
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    options = {}
  ) => {
    const body = { ...update };
    const { baseBooking, onSuccess = null, successToastMessage = null } = options;

    // Optimistic concurrency: when the caller passes the booking it loaded
    // (carrying the server's __version token), send that token plus the original
    // values of the fields being changed. The backend merges independent edits
    // automatically and rejects conflicting ones with 409 CONCURRENT_MODIFICATION.
    if (baseBooking && baseBooking.__version) {
      body.__baseVersion = baseBooking.__version;
      const baseValues = {};
      Object.keys(update).forEach(key => {
        baseValues[key] = baseBooking[key] !== undefined ? baseBooking[key] : null;
      });
      body.__baseValues = baseValues;
    }

    try {
      const responseData = await sendRequest(
        {
          url: '/api/v1/internalBooking/' + bookingId,
          method: 'PATCH',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );

      return responseData;
    } catch (err) {
      // A concurrent edit from another workstation: show one yellow toast here and
      // flag the error so callers can skip their own generic error toast.
      if (err?.code === 'CONCURRENT_MODIFICATION') {
        toast.warning('Daten wurden von einem anderen PC geändert, bitte erneut durchführen.');
        err.isConcurrencyConflict = true;
      }
      throw err;
    }
  };

  const getInternalBookingsByEntertainmentDay = async (
    entertainmentId,
    date,
    showCanceled = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: {
            showCanceled: showCanceled,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getFilteredInternalBookingsByEntertainmentDay = async (
    entertainmentId,
    date,
    filter,
    showCanceled = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { filter: { ...filter } },
          params: {
            showCanceled: showCanceled,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourcesByEntertainmentsDay = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/resources/by/day/${data.date}`,
          method: 'POST',
          body: {
            branches: data.branches,
            entertainments: data.entertainments,
          },
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const addBookingParticipants = async (
    { bookingId, categoryId, countToAdd, keepGroupType, allowOverbook },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const body = { categoryId, countToAdd };
      if (keepGroupType) body.keepGroupType = true;
      if (allowOverbook) body.allowOverbook = true;
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/addParticipants`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const cancelBookingParticipants = async (
    { bookingId, categoryId, countToCancel, keepGroupType, forceReduce },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const body = { categoryId, countToCancel };
      if (keepGroupType) body.keepGroupType = true;
      if (forceReduce) body.forceReduce = true;
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/cancelParticipants`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const spontaneousReduction = async (
    { bookingId, categoryId, countToCancel },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const body = { categoryId, countToCancel };
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/spontaneousReduction`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Server-side preview of the exact MOP-show voucher value (base ticket price minus the per-person
  // ticket-insurance share resolved from the product) plus the payment situation, before confirming.
  const previewSpontaneousReduction = async ({ bookingId, categoryId, countToCancel }) => {
    const body = { countToCancel };
    if (categoryId) body.categoryId = categoryId;
    const responseData = await sendRequest({
      url: `/api/v1/internalBooking/${bookingId}/spontaneousReduction/preview`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body,
    });
    return responseData;
  };

  const cancelBooking = async (
    { bookingId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/cancelBooking`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const cancelBlocker = async (
    { bookingId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/cancelBlocker`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getAvailableCapacity = async ({ bookingId }) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/availableCapacity`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });

      resolve(responseData);
    });
  };

  const updateBookingPackage = async (
    { bookingId, upgradeId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/updatePackage`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { upgradeId },
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      resolve(responseData);
    });
  };

  const getAvailablePackages = async ({ bookingId }) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/updatePackage`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const getAvailableUpsells = async ({ productId, tags = [], bookingId = null }) => {
    return new Promise(async (resolve, reject) => {
      let queryParams = tags.length > 0 ? `?tags=${tags.join(',')}` : '';
      if (bookingId) {
        queryParams += queryParams ? `&bookingId=${bookingId}` : `?bookingId=${bookingId}`;
      }
      const responseData = await sendRequest({
        url: `/api/v1/upgrade/product/${productId}/upsells${queryParams}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const getAvailableShiftDates = async ({ bookingId, month, year, allowOverbook = false }) => {
    return new Promise(async (resolve, reject) => {
      const query = allowOverbook ? '?overbook=true' : '';
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/availableShiftDates/${month}/${year}${query}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const getAvailableShiftTimes = async ({ bookingId, date, allowOverbook = false }) => {
    return new Promise(async (resolve, reject) => {
      const query = allowOverbook ? '?overbook=true' : '';
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/availableShiftTimes/${date}${query}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const shiftBooking = async (
    { bookingId, newDate, allowOverbook },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const body = { newDate };
      if (allowOverbook) body.allowOverbook = true;
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/shiftBooking`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const markCustomerAsPresent = async ({ bookingId, body }) => {
    return new Promise(async (resolve, reject) => {
      const requestConfig = {
        url: `/api/v1/internalBooking/${bookingId}/markCustomerAsPresent`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      };

      // Only add body if lateArrivalReason is provided
      if (body?.lateArrivalReason) {
        requestConfig.body = body;
      }

      const responseData = await sendRequest(requestConfig);
      resolve(responseData);
    });
  };

  const rewindCheckin = async (
    { bookingId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/rewindCheckin`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const rewindCheckout = async (
    { bookingId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/rewindCheckout`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getPrepTimesForEntertainment = async ({ entertainmentId }) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: `/api/v1/entertainment/${entertainmentId}/prepTimes`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const markBookingAsFullyPaid = async (
    { bookingId },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/markBookingAsFullyPaid`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      resolve(responseData);
    });
  };

  const markLineItemsAsPaid = async (
    { bookingId, lineItemIds },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/markLineItemsAsPaid`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { lineItemIds },
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      resolve(responseData);
    });
  };

  const getOpenBriefings = async ({ entertainmentId }) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/getOpenBriefings`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  const getUpcomingNextBookings = async ({ entertainmentId }) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/getUpcomingNextBookings`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      resolve(responseData);
    });
  };

  // Fetch currently present (checked-in, not checked-out) customers of an entertainment.
  // Used to copy customer data into a new booking from the create modal.
  const getPresentCustomersForEntertainment = async entertainmentId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${entertainmentId}/presentCustomers`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const markLTCustomerAsBriefed = async ({ bookingId, briefingLateReason }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const requestConfig = {
          url: `/api/v1/internalBooking/${bookingId}/markLTCustomerAsBriefed`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        };

        // Only add body if briefingLateReason is provided
        if (briefingLateReason) {
          requestConfig.body = { briefingLateReason };
        }

        const responseData = await sendRequest(requestConfig);

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const markBookingAsFinished = async ({ bookingId, experienceRating }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/markAsFinished`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { experienceRating },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const addParticipantMember = async (
    { bookingId, firstName, playerName, cardID, cardStatus },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/addParticipantMember`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { firstName, playerName, cardID, ...(cardStatus ? { cardStatus } : {}) },
        },
        toastMessage || 'Fehler beim Anlegen des Spielers',
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage || 'Spieler angelegts'
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const addParticipantByCardID = async (
    { bookingId, cardID },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/addParticipantByCardID`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { cardID },
        },
        toastMessage || 'Fehler beim Hinzufügen des Spielers',
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage || 'Spieler hinzugefügt'
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getInternalBookingById = async bookingId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getPotentialDuplicateInternalBookings = async bookingId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/potentialDuplicates`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getPizzaOverview = async ({ branchId } = {}) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/pizza/overview',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: {
            branchId,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getPizzaOptions = async ({ branchId } = {}) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/pizza/options',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: {
            branchId,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const addPizzaManualItem = async ({ bookingId, upgradeId, count = 1 }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/pizzaManualItems`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            upgradeId,
            count,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updatePizzaService = async ({
    bookingId,
    plannedServeAt,
    allIncPizzas,
    itemDeliveries,
    comment,
  }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const body = {};
        if (plannedServeAt !== undefined) body.plannedServeAt = plannedServeAt;
        if (allIncPizzas !== undefined) body.allIncPizzas = allIncPizzas;
        if (itemDeliveries !== undefined) body.itemDeliveries = itemDeliveries;
        if (comment !== undefined) body.comment = comment;
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/pizzaService`,
          method: 'PATCH',
          headers: authCtx.getAuthHeaders(),
          body,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updatePizzaDelivery = async ({ bookingId, delivered }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/pizzaDelivery`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            delivered,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getAllInternalBookings = async (
    searchQuery = '',
    page = 1,
    limit = 20,
    selectKeys = [],
    onlyMine = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const params = {};
        if (searchQuery) params.searchQuery = searchQuery;
        if (page) params.page = page;
        if (limit) params.limit = limit;
        if (selectKeys.length > 0) params.selectKeys = selectKeys;
        if (onlyMine) params.onlyMine = onlyMine;
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const addUpsell = async (
    { bookingId, upsellId, count },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/addUpsell`,
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: { upsellId, count },
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updateUpsells = async (
    { bookingId, upsellId, count },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/updateUpsells`,
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: { upsellId, count },
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updateSpecialLineItems = async ({ bookingId, lineItemId, count }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/updateSpecialLineItems`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { lineItemId, count },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const removeVoucherFromBooking = async (
    { bookingId, voucherIdOrCode },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/removeVoucher`,
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: { voucherIdOrCode },
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVoucherMergeCandidates = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/voucherMergeCandidates`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const mergeVoucherBookings = async bookingIds => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/mergeVoucherBookings`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { bookingIds },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateBookingField = async ({ bookingId, field, value }) => {
    return sendRequest({
      url: `/api/v1/internalBooking/${bookingId}`,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authCtx.token,
      },
      body: { [field]: value },
    });
  };

  const resendBookingConfirmation = async (
    bookingId,
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/resendConfirmation`,
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getCustomerConfirmationMailStatus = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/customerConfirmationMailStatus`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const scheduleRegiondoRefreshAfterLinkClick = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/regiondo/schedule-refresh`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const sendNoShowInvoice = async (bookingId, { grantKulanzVoucher = true } = {}) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/sendNoShowInvoice`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { grantKulanzVoucher },
        requiredRights: ['internalBooking:special:sendNoShowInvoice'],
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const skipNoShowInvoice = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/skipNoShowInvoice`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {},
        requiredRights: ['internalBooking:special:sendNoShowInvoice'],
        successToastMessage: 'No-Show-Rechnung als erledigt markiert',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const resendNoShowInvoiceMail = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/resendNoShowInvoiceMail`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {},
        requiredRights: ['internalBooking:special:sendNoShowInvoice'],
        successToastMessage: 'No-Show-Rechnung erneut versendet',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const sendForgottenPaymentInvoice = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/sendForgottenPaymentInvoice`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {},
        requiredRights: ['internalBooking:special:resolveForgotten'],
        successToastMessage: 'Rechnung mit Zahlungsaufforderung wurde versendet',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };
  const sendUnpaidBookingInvoice = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/sendUnpaidBookingInvoice`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {},
        requiredRights: ['internalBooking:special:sendUnpaidBookingInvoice'],
        successToastMessage: 'Rechnung mit Zahlungsaufforderung wurde versendet',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };
  const markNoShow = async (bookingId, contactOutcome) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/markNoShow`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { contactOutcome },
        requiredRights: ['internalBooking:special:markNoShow'],
        successToastMessage: 'Buchung als No-Show markiert',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const revertNoShow = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/revertNoShow`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {},
        requiredRights: ['internalBooking:special:revertNoShow'],
        successToastMessage: 'No-Show zurückgesetzt',
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };
  const clearSlotMember = async ({ slotToken }) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/slot/${slotToken}/clear`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const addNoShowPlaceholder = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/addNoShowPlaceholder`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getAvailableResourcesForShift = async bookingId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/availableResources`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const validateResourceChange = async (bookingId, newResourceId) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/validateResourceChange`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { newResourceId },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const addBookingCategory = async (
    { bookingId, categoryName, count, keepGroupType, allowOverbook },
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    try {
      const body = { categoryName, count };
      if (keepGroupType) body.keepGroupType = true;
      if (allowOverbook) body.allowOverbook = true;
      const responseData = await sendRequest(
        {
          url: `/api/v1/internalBooking/${bookingId}/addCategory`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        toastMessage,
        onStored,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const setOnsiteAccessMode = async ({ bookingId, mode, regeneratePin = false }) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/onsite-access/mode`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { mode, regeneratePin },
      });
      return responseData;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const overrideLineItemPrice = async (bookingId, lineItemId, price) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/lineItems/${lineItemId}/price`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { price },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const addSpecialLineItem = async (bookingId, description, count, price) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/addSpecialLineItem`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { description, count, price },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  return {
    isLoading,
    error,
    getDaysOfMonth,
    getCategories,
    getDurations,
    getStartTimes,
    getPackages,
    getUpsells,
    convertTemporaryReservationToBooking,
    invalidateTemporaryReservation,
    getInternalBookingsByEntertainmentDay,
    getFilteredInternalBookingsByEntertainmentDay,
    getResourcesByEntertainmentsDay,
    updateInternalBooking,
    addBookingParticipants,
    cancelBookingParticipants,
    spontaneousReduction,
    previewSpontaneousReduction,
    cancelBooking,
    cancelBlocker,
    getAvailableCapacity,
    updateBookingPackage,
    getAvailablePackages,
    getAvailableUpsells,
    getAvailableShiftDates,
    getAvailableShiftTimes,
    shiftBooking,
    markCustomerAsPresent,
    rewindCheckin,
    rewindCheckout,
    getPrepTimesForEntertainment,
    markBookingAsFullyPaid,
    markLineItemsAsPaid,
    getOpenBriefings,
    getUpcomingNextBookings,
    getPresentCustomersForEntertainment,
    markLTCustomerAsBriefed,
    markBookingAsFinished,
    getInternalBookingById,
    getPotentialDuplicateInternalBookings,
    getPizzaOverview,
    getPizzaOptions,
    addPizzaManualItem,
    updatePizzaService,
    updatePizzaDelivery,
    getAllInternalBookings,
    addUpsell,
    updateUpsells,
    updateSpecialLineItems,
    updateBookingField,
    resendBookingConfirmation,
    getCustomerConfirmationMailStatus,
    scheduleRegiondoRefreshAfterLinkClick,
    markNoShow,
    revertNoShow,
    sendNoShowInvoice,
    skipNoShowInvoice,
    resendNoShowInvoiceMail,
    sendForgottenPaymentInvoice,
    sendUnpaidBookingInvoice,
    getAvailableResourcesForShift,
    addParticipantMember,
    addParticipantByCardID,
    clearSlotMember,
    addNoShowPlaceholder,
    validateResourceChange,
    removeVoucherFromBooking,
    setOnsiteAccessMode,
    getVoucherMergeCandidates,
    mergeVoucherBookings,
    addBookingCategory,
    overrideLineItemPrice,
    addSpecialLineItem,
  };
};

export default useInternalBooking;
